import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  getDevices,
  batchDeployDevices,
  getFirmwareVersions,
  signalQuality,
} from '../services/devices';
import { getCompanies } from '../services/companies';
import { SegmentedTabs } from '../components/SegmentedTabs';
import { useAuth } from '../context/AuthContext';
import { isSuperAdmin, isCompanyAdmin } from '../services/users';
import DeployModal from '../components/DeployModal';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  Button,
  TableContainer,
  StyledTable,
  Th,
  Td,
  FilterRow,
  Pagination,
  StatusChip,
  DeployedBadge,
  EmptyState,
  LoadingText,
  LinkButton,
  Input,
  Select,
  InlineCode,
  Checkbox,
} from '../components/styled';
import {
  IconChevronUp,
  IconChevronDown,
  IconArrowsSort,
  IconX,
} from '@tabler/icons-react';

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const BatchActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.font.color.secondary};
`;

const CheckboxCell = styled(Td)`
  width: 40px;
  text-align: center;
`;

const CheckboxTh = styled(Th)`
  width: 40px;
  text-align: center;
`;

const FilterInput = styled(Input)`
  height: 28px;
  font-size: ${({ theme }) => theme.font.size.xs};
`;

const FilterSelect = styled(Select)`
  height: 28px;
  font-size: ${({ theme }) => theme.font.size.xs};
`;

const ClearButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.font.color.tertiary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing(0, 1)};
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.font.color.primary};
  }
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const DeviceLink = styled(Link)`
  color: ${({ theme }) => theme.accent.primary};
  text-decoration: none;
  font-size: ${({ theme }) => theme.font.size.sm};

  &:hover {
    text-decoration: underline;
  }
`;

// Deployment batch tag → the spec's `info` semantic chip. Reuses the shared
// StatusChip primitive; only the inline spacing is page-specific.
const BatchBadge = styled(StatusChip)`
  margin-left: ${({ theme }) => theme.spacing(1)};
`;

const SortableTh = styled(Th)`
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    color: ${({ theme }) => theme.accent.primary};
  }
`;

const SortIndicator = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.font.color.tertiary};
`;

const CenteredCell = styled(Td)`
  text-align: center;
`;

function Devices() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [firmwareFilter, setFirmwareFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  // Sliding deployed/undeployed choice (defaults to the active fleet —
  // devices auto-deploy on first contact, so Deployed = everything live).
  const [deployedFilter, setDeployedFilter] = useState('true');
  // Sort state — null means default backend ordering (created_at desc).
  // Cycling on the Last Seen header: null → desc → asc → null.
  const [lastSeenSort, setLastSeenSort] = useState<null | 'asc' | 'desc'>(null);
  // Selected devices are tracked by device_id string — the same identifier
  // the deploy / undeploy / batch endpoints expect in their URL params and
  // request body.
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Cross-company concerns (company filter/column) are super-admin; batch
  // deploy/undeploy is any company admin — the API scopes it to their company.
  const isSuper = isSuperAdmin(user);
  const canManage = isCompanyAdmin(user);

  const sortParam = lastSeenSort ? 'last_seen' : undefined;
  const orderParam = lastSeenSort ?? undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['devices', { page, status: statusFilter, deployed: deployedFilter, search, companyId: companyFilter, firmware: firmwareFilter, mode: modeFilter, sort: sortParam, order: orderParam }],
    queryFn: () => getDevices({
      page,
      limit: 20,
      status: statusFilter || undefined,
      deployed: deployedFilter || undefined,
      search: search || undefined,
      companyId: companyFilter || undefined,
      firmware: firmwareFilter || undefined,
      mode: modeFilter || undefined,
      sort: sortParam,
      order: orderParam,
    }),
  });

  const cycleLastSeenSort = () => {
    setLastSeenSort((prev) => (prev === null ? 'desc' : prev === 'desc' ? 'asc' : null));
    setPage(1);
  };

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: getCompanies,
    enabled: isSuper,
  });
  const companies = companiesData?.companies || [];

  const { data: firmwareVersions } = useQuery({
    queryKey: ['firmware-versions'],
    queryFn: getFirmwareVersions,
  });

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const batchDeployMutation = useMutation({
    mutationFn: ({ deviceIds, deployed, deploymentBatch }: { deviceIds: string[]; deployed: boolean; deploymentBatch?: string }) =>
      batchDeployDevices(deviceIds, deployed, deploymentBatch),
    onSuccess: () => {
      queryClient.invalidateQueries(['devices']);
      setSelectedDevices([]);
      setShowDeployModal(false);
    },
  });

  const handleSelectDevice = (deviceId: string) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDevices.length === devices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(devices.map((d) => d.device_id));
    }
  };

  const devices = data?.devices || [];
  const totalPages = Math.ceil((data?.total || 0) / 20);
  const colSpan = 9 + (canManage ? 1 : 0) + (isSuper ? 1 : 0);

  const statusVariant = (s) => {
    switch (s) {
      case 'online':  return 'green';
      case 'offline': return 'red';
      default:        return 'yellow';
    }
  };

  // power_state from device: 0=active, 1=sleep, 2=deep_sleep
  const modeVariant = (powerState?: number) => {
    switch (powerState) {
      case 0: return 'green';
      case 1: return 'blue';
      case 2: return 'purple';
      default: return 'gray';
    }
  };

  const MODE_LABELS: Record<number, string> = {
    0: 'Active',
    1: 'Sleep',
    2: 'Deep Sleep',
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Never';
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getFirmware = (device) => device.metadata?.state?.firmwareVersion || device.metadata?.firmware || '—';

  // Coulomb-counter battery percentage from the device's last state snapshot.
  // Null on devices without the counter (older firmware) — show an em-dash.
  const getBatteryPct = (device) => {
    const pct = device.metadata?.state?.batteryPct;
    return typeof pct === 'number' ? `${Math.round(pct)}%` : '—';
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Devices</PageTitle>
        <HeaderActions>
          {canManage && selectedDevices.length > 0 && (
            <BatchActions>
              <span>{selectedDevices.length} selected</span>
              <Button
                variant="success"
                size="sm"
                onClick={() => setShowDeployModal(true)}
                disabled={batchDeployMutation.isPending}
              >
                Deploy Selected
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => batchDeployMutation.mutate({ deviceIds: selectedDevices, deployed: false })}
                disabled={batchDeployMutation.isPending}
              >
                Undeploy Selected
              </Button>
            </BatchActions>
          )}
        </HeaderActions>
      </PageHeader>

      <div style={{ marginBottom: 16 }}>
        <SegmentedTabs
          size="sm"
          tabs={[
            { id: 'deployed',   label: 'Deployed' },
            { id: 'undeployed', label: 'Undeployed' },
            { id: 'all',        label: 'All' },
          ]}
          activeId={deployedFilter === 'true' ? 'deployed' : deployedFilter === 'false' ? 'undeployed' : 'all'}
          onChange={(id) => {
            setDeployedFilter(id === 'deployed' ? 'true' : id === 'undeployed' ? 'false' : '');
            setPage(1);
          }}
        />
      </div>

      <TableContainer>
        <StyledTable>
          <thead>
            <FilterRow>
              {canManage && <th />}
              <th>
                <SearchForm onSubmit={handleSearch}>
                  <FilterInput
                    type="text"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search device ID..."
                  />
                  {search && (
                    <ClearButton type="button" onClick={clearSearch} aria-label="Clear search">
                      <IconX size={16} />
                    </ClearButton>
                  )}
                </SearchForm>
              </th>
              {isSuper && (
                <th>
                  <FilterSelect
                    value={companyFilter}
                    onChange={(e) => { setCompanyFilter(e.target.value); setPage(1); }}
                  >
                    <option value="">All Software Providers</option>
                    <option value="unassigned">Unassigned pool</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </FilterSelect>
                </th>
              )}
              <th>
                <FilterSelect
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </FilterSelect>
              </th>
              {/* Deployed/undeployed moved to the sliding control above. */}
              <th />
              <th>
                <FilterSelect
                  value={modeFilter}
                  onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Modes</option>
                  <option value="0">Active</option>
                  <option value="1">Sleep</option>
                  <option value="2">Deep Sleep</option>
                </FilterSelect>
              </th>
              {/* Battery, Signal and Location columns have no filter —
                  placeholders keep the filter row aligned with header/body. */}
              <th />
              <th />
              <th />
              <th>
                <FilterSelect
                  value={firmwareFilter}
                  onChange={(e) => { setFirmwareFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Firmware</option>
                  {(firmwareVersions || []).map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </FilterSelect>
              </th>
              <th />
              <th />
            </FilterRow>
            <tr>
              {canManage && (
                <CheckboxTh>
                  <Checkbox
                    checked={selectedDevices.length === devices.length && devices.length > 0}
                    onChange={handleSelectAll}
                  />
                </CheckboxTh>
              )}
              <Th>Device ID</Th>
              {isSuper && <Th>Company</Th>}
              <Th>Status</Th>
              <Th>Deployed</Th>
              <Th>Mode</Th>
              <Th>Battery</Th>
              <Th>Signal</Th>
              <Th>Location</Th>
              <Th>Firmware</Th>
              <SortableTh
                onClick={cycleLastSeenSort}
                title="Click to sort"
                aria-sort={lastSeenSort === 'asc' ? 'ascending' : lastSeenSort === 'desc' ? 'descending' : 'none'}
              >
                Last Seen
                <SortIndicator>
                  {lastSeenSort === 'desc'
                    ? <IconChevronDown size={14} stroke={2} />
                    : lastSeenSort === 'asc'
                      ? <IconChevronUp size={14} stroke={2} />
                      : <IconArrowsSort size={14} stroke={2} />}
                </SortIndicator>
              </SortableTh>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <CenteredCell colSpan={colSpan}>
                  <LoadingText>Loading devices...</LoadingText>
                </CenteredCell>
              </tr>
            ) : devices.length === 0 ? (
              <tr>
                <CenteredCell colSpan={colSpan}>
                  <EmptyState>
                    {search ? (
                      <>
                        <p>No devices found matching "{search}"</p>
                        <LinkButton onClick={clearSearch}>Clear search</LinkButton>
                      </>
                    ) : (
                      <p>No devices found.</p>
                    )}
                  </EmptyState>
                </CenteredCell>
              </tr>
            ) : (
              devices.map(device => (
                <tr key={device.id}>
                  {canManage && (
                    <CheckboxCell>
                      <Checkbox
                        checked={selectedDevices.includes(device.device_id)}
                        onChange={() => handleSelectDevice(device.device_id)}
                      />
                    </CheckboxCell>
                  )}
                  <Td>
                    <InlineCode>{device.device_id}</InlineCode>
                    {device.deployment_batch && (
                      <BatchBadge variant="info">{device.deployment_batch}</BatchBadge>
                    )}
                  </Td>
                  {isSuper && <Td>{device.company_name || '—'}</Td>}
                  <Td>
                    <StatusChip variant={statusVariant(device.status)}>
                      {device.status}
                    </StatusChip>
                  </Td>
                  <Td>
                    <DeployedBadge deployed={device.deployed} />
                  </Td>
                  <Td>
                    <StatusChip variant={modeVariant(device.metadata?.state?.powerState)}>
                      {MODE_LABELS[device.metadata?.state?.powerState ?? 1] ?? 'Sleep'}
                    </StatusChip>
                  </Td>
                  <Td>{getBatteryPct(device)}</Td>
                  <Td>
                    {(() => {
                      const signal = device.metadata?.state?.signal;
                      const tier = signalQuality(signal);
                      return typeof signal === 'number' ? (
                        <StatusChip variant={tier.variant} title={`${signal} dBm`}>
                          {tier.label}
                        </StatusChip>
                      ) : (
                        '—'
                      );
                    })()}
                  </Td>
                  <Td>
                    {(() => {
                      const geo = device.metadata?.state?.geo;
                      return geo?.city ? (
                        <span title={geo.accuracyM != null ? `±${geo.accuracyM} m (cell tower)` : undefined}>
                          {geo.city}{geo.state ? `, ${geo.state}` : ''}
                        </span>
                      ) : '—';
                    })()}
                  </Td>
                  <Td>{getFirmware(device)}</Td>
                  <Td>{formatLastSeen(device.last_seen)}</Td>
                  <Td>
                    <DeviceLink to={`/devices/${device.device_id}`}>View</DeviceLink>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </StyledTable>
      </TableContainer>

      {!isLoading && devices.length > 0 && (
        <Pagination>
          <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            Previous
          </Button>
          <span>Page {page} of {totalPages || 1}</span>
          <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </Button>
        </Pagination>
      )}

      {showDeployModal && (
        <DeployModal
          devices={devices.filter((d) => selectedDevices.includes(d.device_id))}
          isPending={batchDeployMutation.isPending}
          onConfirm={(deploymentBatch) =>
            batchDeployMutation.mutate({ deviceIds: selectedDevices, deployed: true, deploymentBatch })
          }
          onClose={() => setShowDeployModal(false)}
        />
      )}
    </PageContainer>
  );
}

export default Devices;
